import { useCallback, useEffect, useRef, useState } from 'react';
import { ReportApiError } from './api';
import { isReportDocumentV2, upgradeReportDocument, type ReportDocumentV2 } from './model';

type History = { document: ReportDocumentV2; undo: ReportDocumentV2[]; redo: ReportDocumentV2[] };
export type SaveState = 'unsaved' | 'saved' | 'saving' | 'error' | 'conflict';

/** One serialized writer. Edits made during a save are always sent in the next write. */
export function useReportDraft(
  initial: ReportDocumentV2,
  options: {
    save: (document: ReportDocumentV2) => Promise<void>;
    storageKey: string;
    identityKey?: string;
    legacyStorageKey?: string;
    initiallySaved: boolean;
    readOnly?: boolean;
  }
) {
  const [history, setHistory] = useState<History>({ document: initial, undo: [], redo: [] });
  const [status, setStatus] = useState<SaveState>(options.initiallySaved ? 'saved' : 'unsaved');
  const [error, setError] = useState('');
  const [recovery, setRecovery] = useState<ReportDocumentV2 | null>(null);
  const pendingRecovery = useRef<ReportDocumentV2 | null>(null);
  const legacyRecovery = useRef(false);
  const [reviewPending, setReviewPending] = useState(false);
  const current = useRef(initial);
  const saved = useRef(JSON.stringify(initial));
  const hasSaved = useRef(options.initiallySaved);
  const writer = useRef(options.save);
  writer.current = options.save;
  const running = useRef<Promise<void> | null>(null);
  const queued = useRef(false);
  const mounted = useRef(true);
  const conflict = useRef(false);
  const reportIdentity = `${initial.companyId}:${options.identityKey ?? options.storageKey}`;
  const draftIdentity = useRef(reportIdentity);
  const cacheKey = useRef(options.storageKey);
  const dirty = JSON.stringify(history.document) !== saved.current;

  useEffect(() => {
    mounted.current = true;
    if (draftIdentity.current !== reportIdentity) {
      draftIdentity.current = reportIdentity;
      cacheKey.current = options.storageKey;
      current.current = initial;
      saved.current = JSON.stringify(initial);
      hasSaved.current = options.initiallySaved;
      conflict.current = false;
      queued.current = false;
      running.current = null;
      pendingRecovery.current = null;
      legacyRecovery.current = false;
      setHistory({ document: initial, undo: [], redo: [] });
      setStatus(options.initiallySaved ? 'saved' : 'unsaved');
      setError('');
      setRecovery(null);
      setReviewPending(false);
    } else if (cacheKey.current !== options.storageKey) {
      // A newly saved report adopts its persisted cache namespace without
      // becoming a different document or losing edits made during the save.
      const previousKey = cacheKey.current;
      cacheKey.current = options.storageKey;
      try {
        const previous = localStorage.getItem(previousKey);
        if (previous) {
          const cached = JSON.parse(previous);
          if (isReportDocumentV2(cached) && cached.companyId === initial.companyId) {
            localStorage.setItem(options.storageKey, previous);
            if (localStorage.getItem(previousKey) === previous)
              localStorage.removeItem(previousKey);
          }
        }
      } catch {
        /* Browser storage is optional; the current document and writer remain intact. */
      }
      return () => {
        mounted.current = false;
      };
    }
    try {
      const scoped = localStorage.getItem(options.storageKey);
      const legacy =
        !scoped &&
        options.legacyStorageKey &&
        localStorage.getItem(`${options.legacyStorageKey}:reviewed`) !== 'true'
          ? localStorage.getItem(options.legacyStorageKey)
          : null;
      const raw = scoped || legacy;
      const draft = raw ? JSON.parse(raw) : null;
      if (
        isReportDocumentV2(draft) &&
        draft.companyId === initial.companyId &&
        JSON.stringify(draft) !== saved.current
      ) {
        pendingRecovery.current = draft;
        legacyRecovery.current = !!legacy;
        setRecovery(draft);
      }
    } catch {
      /* Browser storage may be unavailable. The in-memory draft still works. */
    }
    return () => {
      mounted.current = false;
    };
  }, [initial.companyId, reportIdentity, options.storageKey, options.legacyStorageKey]);

  const cache = useCallback((document: ReportDocumentV2) => {
    try {
      localStorage.setItem(cacheKey.current, JSON.stringify(document));
    } catch {
      /* Keep the draft in memory. */
    }
  }, []);

  const applyChange = useCallback(
    (change: (document: ReportDocumentV2) => ReportDocumentV2) => {
      if (options.readOnly) return;
      setHistory((previous) => {
        const document = change(previous.document);
        if (JSON.stringify(document) === JSON.stringify(previous.document)) return previous;
        current.current = document;
        cache(document);
        if (!conflict.current)
          setStatus(
            hasSaved.current && JSON.stringify(document) === saved.current ? 'saved' : 'unsaved'
          );
        return { document, undo: [...previous.undo.slice(-49), previous.document], redo: [] };
      });
    },
    [options.readOnly, cache]
  );

  const update = useCallback(
    (change: (document: ReportDocumentV2) => ReportDocumentV2) => {
      if (pendingRecovery.current) return;
      applyChange(change);
    },
    [applyChange]
  );

  const moveHistory = useCallback(
    (direction: 'undo' | 'redo') => {
      if (options.readOnly || pendingRecovery.current) return;
      setHistory((previous) => {
        const source = previous[direction];
        if (!source.length) return previous;
        const document = source[source.length - 1];
        current.current = document;
        cache(document);
        if (!conflict.current)
          setStatus(
            hasSaved.current && JSON.stringify(document) === saved.current ? 'saved' : 'unsaved'
          );
        return direction === 'undo'
          ? { document, undo: source.slice(0, -1), redo: [...previous.redo, previous.document] }
          : { document, undo: [...previous.undo, previous.document], redo: source.slice(0, -1) };
      });
    },
    [cache, options.readOnly]
  );

  const save = useCallback(async () => {
    if (options.readOnly || pendingRecovery.current || conflict.current) return;
    setReviewPending(false);
    if (running.current) {
      queued.current = true;
      return running.current;
    }
    const identity = reportIdentity;
    const write = writer.current;
    const job = (async () => {
      do {
        if (draftIdentity.current !== identity) return;
        queued.current = false;
        const document = current.current;
        const serialized = JSON.stringify(document);
        if (mounted.current) {
          setStatus('saving');
          setError('');
        }
        try {
          await write(document);
          if (draftIdentity.current !== identity) return;
          saved.current = serialized;
          hasSaved.current = true;
          if (!mounted.current) return;
          const changed = JSON.stringify(current.current) !== serialized;
          if (changed) queued.current = true;
          setStatus(changed ? 'unsaved' : 'saved');
          if (!changed) {
            try {
              if (localStorage.getItem(cacheKey.current) === serialized) {
                localStorage.removeItem(cacheKey.current);
              }
            } catch {
              /* optional cache */
            }
          }
        } catch (failure) {
          if (draftIdentity.current !== identity) return;
          if (!mounted.current) return;
          try {
            const cached = localStorage.getItem(cacheKey.current);
            if (!cached || cached === serialized || cached === JSON.stringify(current.current)) {
              cache(current.current);
            }
          } catch {
            /* The in-memory draft is still retained. */
          }
          conflict.current = failure instanceof ReportApiError && failure.status === 409;
          setStatus(conflict.current ? 'conflict' : 'error');
          setError(
            failure instanceof Error ? failure.message : 'Saving failed. Your draft is retained.'
          );
          queued.current = false;
          return;
        }
      } while (queued.current);
    })();
    running.current = job;
    try {
      await job;
    } finally {
      if (running.current === job) running.current = null;
    }
  }, [cache, options.readOnly, reportIdentity]);

  useEffect(() => {
    if (
      !dirty ||
      status === 'error' ||
      status === 'conflict' ||
      options.readOnly ||
      recovery ||
      reviewPending
    )
      return;
    const timer = window.setTimeout(() => {
      void save();
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [history.document, dirty, save, status, options.readOnly, recovery, reviewPending]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (JSON.stringify(current.current) !== saved.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  return {
    document: history.document,
    update,
    save,
    status,
    error,
    dirty,
    undo: () => moveHistory('undo'),
    redo: () => moveHistory('redo'),
    canUndo: !!history.undo.length && !options.readOnly && !recovery,
    canRedo: !!history.redo.length && !options.readOnly && !recovery,
    recovery,
    reviewPending,
    restore: () => {
      if (!recovery || options.readOnly) return;
      const restored = upgradeReportDocument(recovery);
      applyChange(() => restored);
      if (legacyRecovery.current && options.legacyStorageKey)
        try {
          // Only explicit Restore associates an older company draft with this report.
          // Retain the original cache and prevent it resurfacing after later saves.
          localStorage.setItem(options.storageKey, JSON.stringify(restored));
          localStorage.setItem(`${options.legacyStorageKey}:reviewed`, 'true');
        } catch {
          /* The retained legacy cache remains recoverable. */
        }
      legacyRecovery.current = false;
      pendingRecovery.current = null;
      setReviewPending(true);
      setRecovery(null);
    },
    discardRecovery: () => {
      pendingRecovery.current = null;
      setRecovery(null);
      try {
        if (legacyRecovery.current && options.legacyStorageKey)
          localStorage.setItem(`${options.legacyStorageKey}:reviewed`, 'true');
        if (localStorage.getItem(options.storageKey) === JSON.stringify(recovery)) {
          localStorage.removeItem(options.storageKey);
        }
      } catch {
        /* optional cache */
      }
      legacyRecovery.current = false;
    },
  };
}
